<template>
  <InfoTooltip
    v-if="visible"
    class="validation-summary"
    :variant="hasFailures ? 'error' : 'success'"
    :title="t('global.information')"
    experimental-design
    max-width="550px"
  >
    <template #activator>
      <LISAButton
        :severity="hasFailures ? 'danger' : 'success'"
        :aria-label="t('global.information')"
        :icon="hasFailures ? 'fas fa-triangle-exclamation' : 'fas fa-circle-info'"
        text
      />
    </template>

    <ul v-if="extraValidationRules.length > 0" class="validation-summary__list">
      <li v-for="(rule, index) in extraValidationRules" :key="index">
        <i
          :class="rule.condition ? 'fas fa-check' : 'fas fa-xmark'"
          :style="{
            color: rule.condition ? 'var(--p-green-500)' : 'var(--p-red-500)',
          }"
        />
        <span>{{ rule.message }}</span>
      </li>
    </ul>

    <template v-if="validation.issues.length > 0">
      <hr v-if="extraValidationRules.length > 0" class="validation-summary__divider" />
      <ul class="validation-summary__list validation-summary__list--issues">
        <li v-for="entry in validation.issues" :key="entry.id">
          <i class="fas fa-xmark" />
          <span>{{ formatIssue(entry) }}</span>
        </li>
      </ul>
    </template>
    <div v-else-if="extraValidationRules.length === 0" class="validation-summary__valid-message">
      <i class="fas fa-check" />
      <span>{{ t("validate.valid") }}</span>
    </div>
  </InfoTooltip>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "@/language/translation";
import InfoTooltip from "@/components/shared/InfoTooltip.vue";
import type { AggregatedValidationEntry, FormValidationController } from "@/modules/formValidation";
import type { ExtraValidationRule } from "./ExtraValidationRule";
import LISAButton from "../primevue/LISAButton.vue";

const props = withDefaults(
  defineProps<{
    validation: FormValidationController;
    extraValidationRules?: readonly ExtraValidationRule[];
  }>(),
  { extraValidationRules: () => [] },
);

const { t } = useTranslation();
const visible = computed(() => props.extraValidationRules.length > 0 || props.validation.hasRules);
const failedExtraRules = computed(() =>
  props.extraValidationRules.filter((rule) => !rule.condition),
);
const hasFailures = computed(
  () => failedExtraRules.value.length > 0 || props.validation.issues.length > 0,
);

function formatIssue(entry: AggregatedValidationEntry): string {
  const path = entry.path.length > 0 ? `${entry.path.join(" / ")} / ` : "";
  const label = entry.displayName || entry.name;
  const message =
    entry.status === "pending-validation"
      ? t("validation.pending")
      : t(entry.issue?.messageKey ?? "validation.unexpected", {
          field: label,
          ...entry.issue?.params,
        });
  return `${path}${label}: ${message}`;
}
</script>

<style scoped lang="scss">
.validation-summary__list {
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: baseline;
    gap: 0.25rem;
  }
}

.validation-summary__divider {
  margin: 0.5rem 0;
}
</style>
